import { Text } from 'blaze-ui/Text/Text'
import Styles from './StyleImageLoader.module.scss'
import cx from 'classnames'
import { MediaFile } from 'components/VisualStylePreviewCarousel/VisualStylePreviewCarousel'
import { useState, useEffect, useRef, useCallback } from 'react'
import { ProgressBar } from 'components/ProgressBar/ProgressBar'

export interface StyleImageLoaderProps {
  sourceImage?: MediaFile
  stylesGeneration?: { styledUrl?: string | null; isLoading: boolean; hasFailed: boolean }
  onTransformedImageLoaded?: (loaded: boolean) => void
  withTag?: boolean
  fakeDuration?: number
  staticImageUrl?: string
  staticMode?: boolean
}

const FAKE_PROGRESS_MAX = 95
const FINAL_IMAGE_DELAY = 800

export const StyleImageLoader = ({
  sourceImage,
  stylesGeneration,
  onTransformedImageLoaded,
  withTag = true,
  fakeDuration = 50000,
  staticImageUrl,
  staticMode = false,
}: StyleImageLoaderProps) => {
  const [percentage, setPercentage] = useState(0)
  const [showFinalImage, setShowFinalImage] = useState(staticMode)
  const [imageReady, setImageReady] = useState(staticMode)
  const hasNotifiedRef = useRef(false)
  const hasStartedFinalAnimationRef = useRef(false)
  const fakeProgressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const finalImageRef = useRef<HTMLImageElement>(null)
  const completeProgressRef = useRef<(() => void) | null>(null)

  const transformedImage = staticMode ? staticImageUrl : stylesGeneration?.styledUrl
  const originalImage = sourceImage?.url

  useEffect(() => {
    if (staticMode) return

    const startTime = Date.now()

    const tick = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min((elapsed / fakeDuration) * FAKE_PROGRESS_MAX, FAKE_PROGRESS_MAX)
      setPercentage(Math.round(progress))

      if (progress >= FAKE_PROGRESS_MAX && fakeProgressTimerRef.current !== null) {
        clearInterval(fakeProgressTimerRef.current)
        fakeProgressTimerRef.current = null
      }
    }

    fakeProgressTimerRef.current = setInterval(tick, 250)

    return () => {
      if (fakeProgressTimerRef.current !== null) {
        clearInterval(fakeProgressTimerRef.current)
        fakeProgressTimerRef.current = null
      }
    }
  }, [fakeDuration, staticMode])

  useEffect(() => {
    if (staticMode || !transformedImage || imageReady) return

    const img = new Image()
    img.onload = () => setImageReady(true)
    img.onerror = () => setImageReady(true)
    img.src = transformedImage
  }, [transformedImage, imageReady, staticMode])

  const completeProgress = useCallback(() => {
    setShowFinalImage(true)
    if (!hasNotifiedRef.current && onTransformedImageLoaded) {
      hasNotifiedRef.current = true
      onTransformedImageLoaded(true)
    }
  }, [onTransformedImageLoaded])
  completeProgressRef.current = completeProgress

  useEffect(() => {
    if (staticMode || !transformedImage || !imageReady) return
    if (hasStartedFinalAnimationRef.current) return

    hasStartedFinalAnimationRef.current = true

    if (fakeProgressTimerRef.current !== null) {
      clearInterval(fakeProgressTimerRef.current)
      fakeProgressTimerRef.current = null
    }

    const startPercentage = percentage
    let currentProgress = startPercentage
    let cancelled = false

    const tick = () => {
      if (cancelled) return
      const remainingProgress = 100 - startPercentage
      currentProgress += Math.max(2, remainingProgress * 0.1)

      if (currentProgress >= 100) {
        setPercentage(100)
        setTimeout(() => completeProgressRef.current?.(), FINAL_IMAGE_DELAY)
      } else {
        setPercentage(Math.round(currentProgress))
        setTimeout(tick, 30)
      }
    }

    setTimeout(tick, 30)

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transformedImage, imageReady, staticMode])

  const getCurrentStage = () => {
    if (percentage < 30) return 'Analyzing source image'
    if (percentage < 55) return 'Generating new image'
    if (percentage < 85) return 'Applying style'
    return 'Finalizing'
  }

  const isLoading = !staticMode && !showFinalImage

  return (
    <div className={cx(Styles.loadingContainer, { [Styles.loading]: isLoading })}>
      {!staticMode && originalImage && (
        <img
          src={originalImage}
          alt="transforming original"
          className={cx(Styles.originalImage, { [Styles.visible]: isLoading })}
          onContextMenu={(e) => e?.preventDefault()}
        />
      )}
      {transformedImage && (
        <img
          ref={finalImageRef}
          src={transformedImage}
          alt="real transformed"
          className={cx(Styles.finalImage, { [Styles.visible]: showFinalImage })}
          onContextMenu={(e) => e?.preventDefault()}
        />
      )}
      {withTag && <div className={cx(Styles.imageTag, Styles.generatedTag)}>Generated Image</div>}
      {isLoading && (
        <div className={Styles.progressContainer}>
          <ProgressBar
            percentage={percentage}
            color="var(--light-100)"
            backgroundColor="var(--dark-15)"
            size="xs"
            width="112px"
          />
          <Text color="var(--light-60)">
            {getCurrentStage()}... {percentage}%
          </Text>
        </div>
      )}
    </div>
  )
}
